import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { OrganizationService } from '../../core/data-services/organization.service';
import { AcreageReportService } from '../../core/api-services/acreage-report.service';
import { AcreageReportOrgDetail } from '../../core/response-models/acreage-report-org-detail.model';
import { AcreageReportSummary } from '../../core/response-models/acreage-report-summary.model';
import { AcreageReportFilterOptions } from '../../core/response-models/acreage-report-filter-options.model';
import { OrganizationOption } from '../../core/response-models/organization-option.model';
import { openExternal } from '../../core/utils/external-link-utils';

/**
 * Acreage Reports Admin Page
 *
 * Displays acreage reports. Select "All Organizations" for a cross-org summary,
 * or a specific organization for detailed drill-downs.
 */
@Component({
  selector: 'app-acreage-reports',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './acreage-reports.component.html',
  styleUrl: './acreage-reports.component.scss'
})
export class AcreageReportsComponent implements OnInit {
  loading = false;
  exportLoading = false;
  errorMessage = '';
  successMessage = '';

  // Filters
  filterStartDate = '';
  filterEndDate = '';
  filterOrganization = '';

  // Organizations for dropdown
  organizations: OrganizationOption[] = [];

  // Report data
  orgDetailData: AcreageReportOrgDetail | null = null;
  summaryData: AcreageReportSummary | null = null;

  constructor(
    private acreageReportService: AcreageReportService,
    private organizationService: OrganizationService
  ) {}

  ngOnInit(): void {
    this.initializeDateRange();
    this.loadOrganizations();
  }

  /**
   * Initialize date range to current month (first and last day)
   */
  private initializeDateRange(): void {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    this.filterStartDate = this.formatDateForInput(firstDay);
    this.filterEndDate = this.formatDateForInput(lastDay);
  }

  /**
   * Format date for HTML date input (YYYY-MM-DD)
   */
  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Load organizations for filter dropdown
   */
  async loadOrganizations(): Promise<void> {
    try {
      const page = await firstValueFrom(this.organizationService.getOptions());
      this.organizations = page.items;
    } catch (error: unknown) {
      console.error('Failed to load organizations:', error);
    }
  }

  /**
   * Generate report based on current filters
   */
  generateReport(): void {
    this.loadReportData();
  }

  /**
   * Load report data based on organization selection
   */
  async loadReportData(): Promise<void> {
    if (!this.filterStartDate || !this.filterEndDate) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.orgDetailData = null;
    this.summaryData = null;

    try {
      if (this.filterOrganization) {
        this.orgDetailData = await this.acreageReportService.getOrganizationDetail(
          this.filterOrganization,
          this.filterStartDate,
          this.filterEndDate
        );
      } else {
        this.summaryData = await this.acreageReportService.getSummary(
          this.filterStartDate,
          this.filterEndDate
        );
      }
    } catch (error: unknown) {
      console.error('Failed to load report data:', error);
      this.errorMessage = ApiError.fromHttpError(error).message || 'Failed to load report data';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Export current report to PDF
   */
  async exportToPdf(): Promise<void> {
    this.exportLoading = true;
    this.errorMessage = '';

    try {
      const filters = {
        startDate: this.filterStartDate,
        endDate: this.filterEndDate,
        organizationId: this.filterOrganization
      };

      const isSummary = !this.filterOrganization;
      const reportMode = isSummary ? 'cross-organization-summary' : 'organization-detail';
      const rawData = isSummary ? this.summaryData : this.orgDetailData;

      if (!rawData) {
        this.errorMessage = 'No data to export';
        this.exportLoading = false;
        return;
      }

      const data = { ...rawData, isSummaryMode: isSummary };

      const result = await this.acreageReportService.exportPdf(
        reportMode,
        filters,
        data
      );

      if (result && result.downloadUrl) {
        openExternal(result.downloadUrl);
        this.successMessage = 'PDF generated successfully';
        this.clearSuccessMessage();
      }
    } catch (error: unknown) {
      console.error('PDF export error:', error);
      this.errorMessage = ApiError.fromHttpError(error).message || 'Failed to export PDF';
    } finally {
      this.exportLoading = false;
    }
  }

  /**
   * Clear success message after delay
   */
  private clearSuccessMessage(): void {
    setTimeout(() => {
      this.successMessage = '';
    }, 3000);
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    const str = String(dateString);
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const date = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
    const date = new Date(str);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  /**
   * Format number with commas
   */
  formatNumber(num: number | null | undefined): string {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  /**
   * Get organization name by ID
   */
  getOrganizationName(organizationId: string): string {
    const org = this.organizations.find(o => o.id === organizationId);
    return org?.name || organizationId;
  }
}
